import { z } from 'zod';

// Webinars (/api/webinars) and unmatched attendees (/api/attendance). Schemas check shape only:
// CSV parsing, contact lookups, Zoom id rules and the document kinds stay in webinarService.
// The lists below mirror models/UnmatchedAttendee and models/WebinarQuestion (the tests compare them).

export const UnmatchedAttendeeStatus = z.enum(['new', 'linked', 'guest', 'ignored']);
export type UnmatchedAttendeeStatus = z.infer<typeof UnmatchedAttendeeStatus>;

export const UnmatchedResolveAction = z.enum(['link', 'guest', 'ignore']);
export type UnmatchedResolveAction = z.infer<typeof UnmatchedResolveAction>;

export const WebinarAnswerMode = z.enum(['live', 'text']);
export type WebinarAnswerMode = z.infer<typeof WebinarAnswerMode>;

// ---------------------------------------------------------------- requests

export const WebinarIdParams = z.strictObject({ id: z.guid() });
export type WebinarIdParams = z.input<typeof WebinarIdParams>;

/** Unknown kinds answer 404 Unknown document. */
export const WebinarDocumentParams = z.strictObject({ id: z.guid(), kind: z.string() });
export type WebinarDocumentParams = z.input<typeof WebinarDocumentParams>;

/** A userId that is not a member answers 404 Member not found. */
export const AttendanceLetterParams = z.strictObject({ userId: z.string() });
export type AttendanceLetterParams = z.input<typeof AttendanceLetterParams>;

/** only: comma-separated session keys to include. */
export const AttendanceLetterQuery = z.strictObject({ only: z.string().optional() });
export type AttendanceLetterQuery = z.input<typeof AttendanceLetterQuery>;

/** PUT /api/webinars/:id/production. Speakers and moderator are contact ids; bios are keyed by them. */
export const WebinarProductionRequest = z.strictObject({
  speakerIds: z.array(z.string()).nullish(),
  moderatorId: z.string().nullish(),
  coordinator: z.string().nullish(),
  abstract: z.string().nullish(),
  bios: z.record(z.string(), z.string().nullable()).nullish(),
  zoomWebinarId: z.union([z.string(), z.number()]).nullish(),
});
export type WebinarProductionRequest = z.input<typeof WebinarProductionRequest>;

/** POST /api/webinars/:id/attendance | polls | qa — a Zoom report CSV; a dry run unless confirm is true. */
export const WebinarUploadRequest = z.strictObject({
  fileName: z.string().nullish(),
  content: z.string({ error: 'content (the CSV text) is required' }),
  confirm: z.boolean().optional(),
});
export type WebinarUploadRequest = z.input<typeof WebinarUploadRequest>;

/** GET /api/attendance/unmatched?status= (default new; all for every status). */
const UnmatchedStatusFilter = z.union([UnmatchedAttendeeStatus, z.literal('all')]);
export const UnmatchedListQuery = z.strictObject({
  status: z.union([UnmatchedStatusFilter, z.array(UnmatchedAttendeeStatus)]).optional(),
});
export type UnmatchedListQuery = z.input<typeof UnmatchedListQuery>;

/** POST /api/attendance/unmatched/resolve; link needs userId or memberEmail (the route checks). */
export const UnmatchedResolveRequest = z.strictObject({
  email: z.string().min(1, 'is required'),
  action: UnmatchedResolveAction,
  userId: z.union([z.guid(), z.literal('')]).nullish(),
  memberEmail: z.string().nullish(),
});
export type UnmatchedResolveRequest = z.input<typeof UnmatchedResolveRequest>;
